import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, Loader2, X, Download, RefreshCw, Archive } from "lucide-react";
import { toast } from "sonner";

export const DOC_CATEGORIES = [
  { value: "acta_constitutiva", label: "Acta constitutiva" },
  { value: "poder_notarial", label: "Poder notarial" },
  { value: "identificacion", label: "Identificación" },
  { value: "csf", label: "Constancia de Situación Fiscal" },
  { value: "opinion_32d", label: "Opinión SAT 32-D" },
  { value: "padron_contratistas", label: "Padrón de contratistas" },
  { value: "datos_bancarios", label: "Datos bancarios" },
  { value: "complementario", label: "Complementario" },
];
const catLabel = (v) => DOC_CATEGORIES.find((c) => c.value === v)?.label || v;

const statusBadge = (d) => {
  if (d.status === "sustituido") return <span className="text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase text-slate-400 bg-slate-500/10">Sustituido</span>;
  if (d.expired || d.status === "vencido") return <span className="text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase text-red-400 bg-red-400/10">Vencido</span>;
  return <span className="text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase text-green-400 bg-green-400/10">Vigente</span>;
};

const EMPTY = { name: "", category: "complementario", issue_date: "", expiry_date: "", responsible: "", observations: "" };

export default function CorporateDocuments({ onMutate }) {
  const { user } = useAuth();
  const canArchive = ["admin", "contador"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {form, file, versionOf?}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/legal/documents");
      setItems(data);
      onMutate?.();
    } catch { toast.error("No se pudieron cargar los documentos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!modal.file) return toast.error("Selecciona un archivo");
    if (!modal.versionOf && !modal.form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", modal.file);
      if (modal.versionOf) {
        if (modal.form.issue_date) fd.append("issue_date", modal.form.issue_date);
        if (modal.form.expiry_date) fd.append("expiry_date", modal.form.expiry_date);
        fd.append("observations", modal.form.observations || "");
        await api.post(`/legal/documents/${modal.versionOf}/new-version`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        Object.entries(modal.form).forEach(([k, v]) => { if (v) fd.append(k, v); });
        await api.post("/legal/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Documento guardado");
      setModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/legal/documents/${d.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: d.content_type }));
      window.open(url, "_blank");
    } catch { toast.error("No se pudo descargar"); }
  };

  const archive = async (d) => {
    if (!confirm(`¿Archivar "${d.name}" v${d.version}? Dejará de aparecer en la lista.`)) return;
    try {
      await api.post(`/legal/documents/${d.id}/archive`);
      toast.success("Documento archivado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo archivar"); }
  };

  const shown = filter ? items.filter((d) => d.category === filter) : items;
  const latestByGroup = {};
  items.forEach((d) => {
    const g = d.group_id || d.id;
    if (!latestByGroup[g] || d.version > latestByGroup[g]) latestByGroup[g] = d.version;
  });
  const isLatest = (d) => (latestByGroup[d.group_id || d.id] || d.version) === d.version;

  if (loading) return <div className="p-8 text-center text-slate-400">Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="legal-documents-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="doc-new-btn" onClick={() => setModal({ form: { ...EMPTY }, file: null })}
          className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
          <Upload className="w-4 h-4" /> Subir documento
        </button>
        <select data-testid="doc-category-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="input-cyber text-xs">
          <option value="">Todas las categorías</option>
          {DOC_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      {shown.length === 0 && <div className="text-sm text-slate-500 italic">Sin documentos.</div>}
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b" style={{ borderColor: "var(--border)" }}>
              {["Documento", "Categoría", "Ver.", "Emisión", "Vigencia", "Estado", "Responsable", ""].map((h) => (
                <th key={h} className="label-mono px-3 py-2">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((d) => (
              <tr key={d.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`doc-row-${d.id}`}>
                <td className="px-3 py-2">
                  <div className="text-slate-200">{d.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono truncate max-w-[180px]" title={d.sha256}>sha256: {(d.sha256 || "").slice(0, 12)}…</div>
                </td>
                <td className="px-3 py-2 text-slate-300 text-xs">{catLabel(d.category)}</td>
                <td className="px-3 py-2 font-mono text-cyan-300">v{d.version}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-300">{d.issue_date ? fmtDate(d.issue_date) : "—"}</td>
                <td className={`px-3 py-2 font-mono text-xs ${d.expired ? "text-red-400" : "text-slate-300"}`}>{d.expiry_date ? fmtDate(d.expiry_date) : "—"}</td>
                <td className="px-3 py-2">{statusBadge(d)}</td>
                <td className="px-3 py-2 text-xs text-slate-400">{d.responsible || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <button title="Descargar" data-testid={`doc-download-${d.id}`} onClick={() => download(d)} className="text-cyan-300 hover:text-cyan-100"><Download className="w-4 h-4" /></button>
                    {isLatest(d) && (
                      <button title="Nueva versión" data-testid={`doc-version-${d.id}`}
                        onClick={() => setModal({ form: { ...EMPTY, name: d.name }, file: null, versionOf: d.id })}
                        className="text-amber-300 hover:text-amber-100"><RefreshCw className="w-4 h-4" /></button>
                    )}
                    {canArchive && (
                      <button title="Archivar" data-testid={`doc-archive-${d.id}`} onClick={() => archive(d)} className="text-slate-500 hover:text-red-400"><Archive className="w-4 h-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="panel w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="doc-modal">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-slate-100">{modal.versionOf ? "Nueva versión" : "Subir documento"}</div>
              <button onClick={() => setModal(null)}><X className="w-4 h-4 text-slate-400" /></button>
            </div>
            {!modal.versionOf && (
              <>
                <div>
                  <label className="label-mono block mb-1">Nombre *</label>
                  <input data-testid="doc-form-name" value={modal.form.name} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, name: e.target.value } }))} className="w-full input-cyber" />
                </div>
                <div>
                  <label className="label-mono block mb-1">Categoría</label>
                  <select data-testid="doc-form-category" value={modal.form.category} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, category: e.target.value } }))} className="w-full input-cyber">
                    {DOC_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-mono block mb-1">Fecha de emisión</label>
                <input type="date" value={modal.form.issue_date} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, issue_date: e.target.value } }))} className="w-full input-cyber" />
              </div>
              <div>
                <label className="label-mono block mb-1">Vigencia (si aplica)</label>
                <input type="date" value={modal.form.expiry_date} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, expiry_date: e.target.value } }))} className="w-full input-cyber" />
              </div>
            </div>
            {!modal.versionOf && (
              <div>
                <label className="label-mono block mb-1">Responsable</label>
                <input value={modal.form.responsible} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, responsible: e.target.value } }))} className="w-full input-cyber" />
              </div>
            )}
            <div>
              <label className="label-mono block mb-1">Observaciones</label>
              <textarea rows={2} value={modal.form.observations} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, observations: e.target.value } }))} className="w-full input-cyber" />
            </div>
            <div>
              <label className="label-mono block mb-1">Archivo (PDF o imagen) *</label>
              <input data-testid="doc-form-file" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setModal((m) => ({ ...m, file: e.target.files?.[0] || null }))} className="w-full text-xs text-slate-300" />
            </div>
            <button data-testid="doc-save-btn" onClick={save} disabled={saving}
              className="btn-primary w-full py-2 text-xs uppercase tracking-widest flex items-center justify-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
